import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info' | 'reminder'
  title: string
  message?: string | undefined
  duration?: number | undefined
  persistent?: boolean | undefined
  // Activity reminder fields
  activityId?: string
  activityType?: string
  dueAt?: string
  entityType?: 'deal' | 'contact' | 'company' | 'outreach_reply' | 'wa_conversation'
  entityId?: string
  entityName?: string
}

export const useNotificationStore = defineStore('notifications', () => {
  const notifications = ref<Notification[]>([])

  function addNotification(notification: Omit<Notification, 'id'>): string {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9)
    const newNotification: Notification = {
      id,
      duration: 5000,
      ...notification,
    }
    
    notifications.value.push(newNotification)
    
    // Auto remove if not persistent
    if (!newNotification.persistent) {
      setTimeout(() => {
        removeNotification(id)
      }, newNotification.duration)
    }
    
    return id
  }

  function removeNotification(id: string): void {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
    }
  }

  function clearAll(): void {
    notifications.value = []
  }

  // Convenience methods
  function success(title: string, message?: string, options?: Partial<Notification>): string {
    return addNotification({
      type: 'success',
      title,
      message,
      ...options,
    })
  }

  function error(title: string, message?: string, options?: Partial<Notification>): string {
    return addNotification({
      type: 'error',
      title,
      message,
      duration: 8000, // Errors stay longer
      ...options,
    })
  }

  function warning(title: string, message?: string, options?: Partial<Notification>): string {
    return addNotification({
      type: 'warning',
      title,
      message,
      ...options,
    })
  }

  function info(title: string, message?: string, options?: Partial<Notification>): string {
    return addNotification({
      type: 'info',
      title,
      message,
      ...options,
    })
  }

  function reminder(payload: {
    title: string
    body: string
    activityId?: string
    activityType?: string
    dueAt?: string
    entityType?: 'deal' | 'contact' | 'company' | 'outreach_reply' | 'wa_conversation'
    entityId?: string
    entityName?: string
  }): string {
    const notification: Omit<Notification, 'id'> = {
      type: 'reminder',
      title: payload.title,
      message: payload.body,
      duration: 20000,
    }
    if (payload.activityId) notification.activityId = payload.activityId
    if (payload.activityType) notification.activityType = payload.activityType
    if (payload.dueAt) notification.dueAt = payload.dueAt
    if (payload.entityType) notification.entityType = payload.entityType
    if (payload.entityId) notification.entityId = payload.entityId
    if (payload.entityName) notification.entityName = payload.entityName
    return addNotification(notification)
  }

  return {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    success,
    error,
    warning,
    info,
    reminder,
  }
})